/**
 * Plays back a social post's media for review — every item, one at a time, with
 * video controls. Used by the Social Posts list, the calendar's post details and
 * the template list, so a post can be checked after it is scheduled or published
 * without opening Instagram or Facebook.
 *
 * The URLs are the post's own media (the files uploaded for it), not a copy, so
 * what plays here is exactly what was sent.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';

export interface PreviewMediaItem {
  type: 'image' | 'video';
  url: string;
  /** A video's cover or poster frame, shown before it plays. */
  thumbnail?: string | null;
}

/** Zernio's media items, whose type and URL can be missing, as previewable items. */
export function previewItemsFromZernio(
  items: Array<{ type: 'image' | 'video' | null; url: string | null; thumbnail?: string | null }> | null | undefined,
): PreviewMediaItem[] {
  return (items ?? []).flatMap((m) => (m.url && m.type ? [{ type: m.type, url: m.url, thumbnail: m.thumbnail ?? null }] : []));
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  caption?: string;
  items: PreviewMediaItem[];
  /** Shown under the media, e.g. status and platforms. */
  footer?: ReactNode;
}

/**
 * The viewer on its own — one item at a time, video with controls, arrows and
 * dots for a carousel. Embeddable in any dialog that already has its own layout.
 */
export function SocialMediaCarousel({ items, className }: { items: PreviewMediaItem[]; className?: string }) {
  const playable = items.filter((m) => m.url);
  const [index, setIndex] = useState(0);

  // Back to the first item whenever a different post is shown.
  useEffect(() => {
    setIndex(0);
  }, [items]);

  const current = playable[Math.min(index, playable.length - 1)];
  const many = playable.length > 1;

  if (!current) {
    return <p className={cn('py-10 text-center text-sm text-slate-500', className)}>This post has no photos or videos.</p>;
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative flex items-center justify-center rounded-lg bg-black">
        {current.type === 'video' ? (
          <video
            key={current.url}
            src={current.url}
            poster={current.thumbnail ?? undefined}
            controls
            playsInline
            preload="metadata"
            className="max-h-[65vh] w-full object-contain"
          />
        ) : (
          <img key={current.url} src={current.url} alt="" className="max-h-[65vh] w-full object-contain" />
        )}

        {many && (
          <>
            <button
              type="button"
              aria-label="Previous item"
              disabled={index === 0}
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 text-slate-700 shadow hover:bg-white disabled:opacity-30"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next item"
              disabled={index >= playable.length - 1}
              onClick={() => setIndex((i) => Math.min(playable.length - 1, i + 1))}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 text-slate-700 shadow hover:bg-white disabled:opacity-30"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {many && (
        <div className="flex items-center justify-center gap-1.5">
          {playable.map((m, i) => (
            <button
              key={`${m.url}-${i}`}
              type="button"
              aria-label={`Show item ${i + 1}`}
              onClick={() => setIndex(i)}
              className={cn('h-2 w-2 rounded-full', i === index ? 'bg-blue-600' : 'bg-slate-300 hover:bg-slate-400')}
            />
          ))}
          <span className="ml-2 text-xs text-slate-500">{index + 1} / {playable.length}</span>
        </div>
      )}
    </div>
  );
}

export function SocialMediaPreviewModal({ isOpen, onClose, title, caption, items, footer }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title || 'Post preview'} size="lg">
      <div className="space-y-3">
        {isOpen && <SocialMediaCarousel items={items} />}
        {caption && <p className="whitespace-pre-wrap break-words text-sm text-slate-700">{caption}</p>}
        {footer}
      </div>
    </Modal>
  );
}
